import Loading from "@/pages/loading";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import { MerchantInstallmentCalendarType } from "@payping/api/app/api/bnplMerchantTypes";
import { bnplMerchantGetInstallmentCalendar } from "@payping/redux/actions";
import { moment } from "@payping/utils/dateUtils";
import { Button, Card, Flex, Form, Grid, Select, Typography, useMessagedAsync } from "@payping/web-components";
import { useEffect, useRef } from "react";
import { buildUrl } from "@payping/utilities-shared/url";
import PageComponentForHandlingCrispInMobile from "@/components/mobile/HandlingCrispInMobile";
import Calender from "./Calender";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { LANGUAGES, URLS } from "@payping/utilities-shared/consts";
import { useAtom } from "jotai";
import { selectAtom } from "jotai/utils";
import { dashboardPreferencesAtom } from "@/core/state";

const languageAtom = selectAtom(dashboardPreferencesAtom, (val) => val.language);

interface Filters {
  month: number;
  year: number;
}

const BnplFinancialCalenderPage = () => {
  const { t } = useAppTranslation();
  const [language] = useAtom(languageAtom);

  const momentNow = useRef(moment());

  const momentTime = useRef<Record<typeof language, { year: number; month: number; day: number }>>({
    en: {
      year: momentNow.current.year(),
      month: momentNow.current.month(),
      day: momentNow.current.date(),
    },
    fa: {
      year: momentNow.current.jYear(),
      month: momentNow.current.jMonth(),
      day: momentNow.current.jDate(),
    },
  });
  const momentTimeLocal = momentTime.current[language];

  const [form] = Form.useForm<Filters>();
  const { md, xl } = Grid.useBreakpoint();
  const dispatch = useAppDispatch();

  const registerDateTime = useAppSelector((state) => state.user.registerDateTime);

  const startYear = (() => {
    if (registerDateTime) {
      return language === LANGUAGES.ENGLISH ? moment(registerDateTime).year() : moment(registerDateTime).jYear();
    }
    return momentTimeLocal.year - 15;
  })();

  const yearsRange = (() => {
    const years = [];
    // years after this year
    const yearsAfter = 10;
    for (let year = startYear; year <= momentTimeLocal.year + yearsAfter; year += 1) {
      years.push(year);
    }
    return years;
  })();

  const monthWatch = Form.useWatch("month", form) ?? momentTimeLocal.month;
  const yearWatch = Form.useWatch("year", form) ?? momentTimeLocal.year;

  useEffect(() => {
    form.setFieldValue("year", momentTimeLocal.year);
    form.setFieldValue("month", momentTimeLocal.month);
  }, [form, language, momentTimeLocal.month, momentTimeLocal.year]);

  const calenderState = useMessagedAsync(
    async () => {
      if (language === LANGUAGES.ENGLISH) {
        const toJalali = moment(`${yearWatch}-${monthWatch + 1}-1`, "YYYY-MM-DD");
        const res = await dispatch(
          bnplMerchantGetInstallmentCalendar(
            MerchantInstallmentCalendarType.Monthly,
            toJalali.jYear(),
            toJalali.jMonth() + 1,
          ),
        );
        return res;
      }
      const res = await dispatch(
        bnplMerchantGetInstallmentCalendar(MerchantInstallmentCalendarType.Monthly, yearWatch, monthWatch + 1),
      );
      return res;
    },
    [yearWatch, monthWatch],
    { success: false, error: true },
  );

  const handleClickToday = () => {
    form.setFieldValue("month", momentTimeLocal.month);
    form.setFieldValue("year", momentTimeLocal.year);
  };

  const loadingState = calenderState.loading || calenderState.error;
  return (
    <PageComponentForHandlingCrispInMobile>
      <Flex vertical gap={"large"} className="tw-w-full">
        <Form<Filters>
          className="tw-w-full"
          form={form}
          initialValues={{
            month: momentTimeLocal.month,
            year: momentTimeLocal.year,
          }}
        >
          <Flex justify="space-between" align="center" gap={"small"}>
            <Flex align="center" gap={"small"}>
              <Form.Item<Filters> name={"month"} noStyle>
                {language === LANGUAGES.FARSI ? (
                  <Select
                    className="tw-w-28"
                    options={(t("common.solarMonths", { returnObjects: true }) as string[]).map((item, index) => ({
                      label: item,
                      value: index,
                    }))}
                  />
                ) : null}
                {language === LANGUAGES.ENGLISH ? (
                  <Select
                    className="tw-w-28"
                    options={(t("common.gregorianMonths", { returnObjects: true }) as string[]).map((item, index) => ({
                      label: item,
                      value: index,
                    }))}
                  />
                ) : null}
              </Form.Item>
              <Form.Item<Filters> name={"year"} noStyle>
                <Select
                  className="tw-w-24"
                  options={yearsRange.map((item) => ({
                    label: item,
                    value: item,
                  }))}
                />
              </Form.Item>
            </Flex>
            <Button onClick={handleClickToday} type="default">
              {t("common.today")}
            </Button>
          </Flex>
        </Form>
        <Typography.Title
          level={5}
          className="!tw-m-0"
        >{`${(t("common.solarMonths", { returnObjects: true }) as string[])[monthWatch]} ${yearWatch}`}</Typography.Title>
        {loadingState ? (
          <Loading />
        ) : (
          <>
            <Flex align="center" gap={"middle"} className="tw-w-full" vertical={!md}>
              <Card.Ant className="tw-w-full">
                <Flex vertical={md} className="tw-w-full" gap={"large"} justify={!md ? "space-between" : undefined}>
                  <Flex align="center" gap={"small"}>
                    {md ? <div className="tw-h-3 tw-w-3 tw-rounded-full tw-bg-color-success" /> : null}
                    <Typography.Text type="secondary">{t("shop.bnpl.installmentStatus.completed")}</Typography.Text>
                  </Flex>
                  <Flex align="center" justify="space-between">
                    {xl ? (
                      <img
                        src={buildUrl(URLS.STORAGE_URL, "dashboard-app/bnpl-calender-paid.svg").toString()}
                        className="tw-h-8 tw-w-9 tw-object-cover"
                      />
                    ) : null}
                    <Flex align="center">
                      <Typography.Title className="!tw-m-0" level={5}>
                        {formatLocalizedNumber({ value: calenderState.value?.totalPaidAmount ?? 0 })}
                      </Typography.Title>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </Flex>
                </Flex>
              </Card.Ant>
              <Card.Ant className="tw-w-full">
                <Flex vertical={md} className="tw-w-full" gap={"large"} justify={!md ? "space-between" : undefined}>
                  <Flex align="center" gap={"small"}>
                    {md ? <div className="tw-h-3 tw-w-3 tw-rounded-full tw-bg-color-primary" /> : null}
                    <Typography.Text type="secondary">{t("common.ahead")}</Typography.Text>
                  </Flex>
                  <Flex align="center" justify="space-between">
                    {xl ? (
                      <img
                        src={buildUrl(URLS.STORAGE_URL, "dashboard-app/bnpl-calender-incoming.svg").toString()}
                        className="tw-h-8 tw-w-9 tw-object-cover"
                      />
                    ) : null}

                    <Flex align="center">
                      <Typography.Title className="!tw-m-0" level={5}>
                        {formatLocalizedNumber({ value: calenderState.value?.totalDueAmount ?? 0 })}
                      </Typography.Title>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </Flex>
                </Flex>
              </Card.Ant>
              <Card.Ant className="tw-w-full">
                <Flex vertical={md} className="tw-w-full" gap={"large"} justify={!md ? "space-between" : undefined}>
                  <Flex align="center" gap={"small"}>
                    {md ? <div className="tw-h-3 tw-w-3 tw-rounded-full tw-bg-color-error" /> : null}
                    <Typography.Text type="secondary">{t("shop.bnpl.installmentStatus.delayed")}</Typography.Text>
                  </Flex>
                  <Flex align="center" justify="space-between">
                    {xl ? (
                      <img
                        src={buildUrl(URLS.STORAGE_URL, "dashboard-app/bnpl-calender-delayed.svg").toString()}
                        className="tw-h-8 tw-w-9 tw-object-cover"
                      />
                    ) : null}
                    <Flex align="center">
                      <Typography.Title className="!tw-m-0" level={5}>
                        {formatLocalizedNumber({ value: calenderState.value?.totalDelayedAmount ?? 0 })}
                      </Typography.Title>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </Flex>
                </Flex>
              </Card.Ant>
            </Flex>
            <Calender month={monthWatch} year={yearWatch} day={momentTimeLocal.day} />
          </>
        )}
      </Flex>
    </PageComponentForHandlingCrispInMobile>
  );
};
export default BnplFinancialCalenderPage;
